import { AccessorKeyColumnDef } from '@tanstack/react-table';

/* Shared */
import { Helpers } from 'application/Shared/Helpers';
import { debug } from 'application/Shared/Utils';

import { ColumnDef } from '../types';

/**
 * Filters row data based on a search text and column configurations.
 *
 * @param props
 * @param props.rowData - The array of data objects to be filtered.
 * @param props.columns - The array of column configurations.
 * @param props.searchText - The text to search for in the row data.
 * @param props.clientSideFilters - An object containing client-side filters to apply.
 * @returns An array of filtered data objects.
 */
export function filterRowData<TData, TValue>({
    rowData,
    columns,
    searchText,
    clientSideFilters
}: {
    rowData: TData[];
    columns: ColumnDef<TData, TValue>[];
    searchText: string;
    clientSideFilters: Record<string, string[]>;
}): TData[] {
    // If no search text is provided, return the original data
    if (!searchText && Object.keys(clientSideFilters).length === 0)
        return rowData;

    let searchRegex: RegExp;
    try {
        // Create a case-insensitive regular expression for matching
        searchRegex = new RegExp(searchText, 'i');
    } catch (e) {
        debug.w('Invalid RegExp in search bar', e);
    }

    /**
     * Checks if a value matches the search criteria.
     *
     * @param value - The value to check.
     * @returns True if the value matches the search criteria, false otherwise.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- it's a generic function iterating on unknown data
    const checkValue = (value: any): boolean => {
        if (value == null) return false;
        if (typeof value === 'object') {
            // Recursively check all values in the object
            return Object.values(value).some((v) => checkValue(v));
        }
        // Perform a case-insensitive match
        return String(value).match(searchRegex) !== null;
    };

    // Filter columns to only include those with enableGlobalFilter set to true,
    // if enableGlobalFilter is not set to true, it will be considered as true.
    const filteredColumns = columns.filter(
        ({ enableGlobalFilter = true }) => enableGlobalFilter
    );

    const clientSideFilteringColumns = columns.filter(
        (column) => column.meta?.filter?.clientSideFiltering
    );

    // If no columns are enabled for filtering, return the original data
    if (!filteredColumns.length && !clientSideFilteringColumns.length)
        return rowData;

    // Filter the row data
    return rowData.filter((row) => {
        const searchRowFound =
            filteredColumns.length === 0 ||
            filteredColumns.some((column) => {
                const { accessorKey } = column as AccessorKeyColumnDef<TData>;
                if (!accessorKey) return false;
                const value = Helpers.getNestedValue(
                    row,
                    accessorKey as string
                );
                return checkValue(value);
            });

        const clientSideFilteringMatch = clientSideFilteringColumns.every(
            (column) => {
                const filterFn = column.meta?.filter?.filterFn;
                if (!filterFn) return true;
                const { id } = column as AccessorKeyColumnDef<TData>;
                const filteredValues = clientSideFilters[id as string];
                if (!filteredValues) return true;
                return filterFn(row, filteredValues);
            }
        );
        return searchRowFound && clientSideFilteringMatch;
    });
}
